import { motion } from 'framer-motion';
import {
  Calendar,
  FileText,
  FolderOpen,
  Table,
  Clock,
  Sparkles,
  Bell,
  Video,
  Mail,
  Zap,
  Brain,
  Timer,
  Globe,
  Shield,
  BarChart3,
  ListTodo,
  Share2,
  Repeat,
  Search,
  RefreshCw,
  Plus,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useThemeStore } from '../store/theme';

// Automation feature definitions
interface AutomationFeature {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  level: 'basic' | 'intermediate' | 'advanced';
}

const calendarAutomations: AutomationFeature[] = [
  { id: 'auto-create-events', title: 'Auto-create Events', description: 'Create events from form submissions or email triggers', icon: <Plus className="w-4 h-4" />, level: 'basic' },
  { id: 'custom-reminders', title: 'Custom Reminders', description: 'Send reminders before meetings with custom timing', icon: <Bell className="w-4 h-4" />, level: 'basic' },
  { id: 'auto-decline', title: 'Auto-Decline Events', description: 'Automatically decline events outside working hours', icon: <XCircle className="w-4 h-4" />, level: 'basic' },
  { id: 'sync-calendars', title: 'Sync Calendars', description: 'Sync events between multiple calendars', icon: <RefreshCw className="w-4 h-4" />, level: 'basic' },
  { id: 'smart-scheduling', title: 'Smart Scheduling', description: 'Auto-schedule meetings based on availability', icon: <Clock className="w-4 h-4" />, level: 'intermediate' },
  { id: 'recurring-templates', title: 'Recurring Templates', description: 'Create recurring events from templates', icon: <Repeat className="w-4 h-4" />, level: 'intermediate' },
  { id: 'auto-video-links', title: 'Auto Video Links', description: 'Automatically add Meet/Zoom links', icon: <Video className="w-4 h-4" />, level: 'intermediate' },
  { id: 'follow-up-emails', title: 'Follow-up Emails', description: 'Send automated follow-up emails', icon: <Mail className="w-4 h-4" />, level: 'intermediate' },
  { id: 'focus-time-blocks', title: 'Focus Time Blocks', description: 'Auto-block focus time daily', icon: <Shield className="w-4 h-4" />, level: 'intermediate' },
  { id: 'ai-optimal-times', title: 'AI Optimal Times', description: 'AI suggests optimal meeting times', icon: <Brain className="w-4 h-4" />, level: 'advanced' },
  { id: 'conflict-resolution', title: 'Conflict Resolution', description: 'Auto-reschedule conflicts', icon: <Zap className="w-4 h-4" />, level: 'advanced' },
  { id: 'meeting-analytics', title: 'Meeting Analytics', description: 'Track meeting time by category', icon: <BarChart3 className="w-4 h-4" />, level: 'advanced' },
  { id: 'calendar-to-tasks', title: 'Calendar to Tasks', description: 'Auto-create tasks from events', icon: <ListTodo className="w-4 h-4" />, level: 'advanced' },
  { id: 'timezone-scheduling', title: 'Timezone-Aware', description: 'Smart scheduling across timezones', icon: <Globe className="w-4 h-4" />, level: 'advanced' },
  { id: 'buffer-automation', title: 'Buffer Time', description: 'Auto-add prep time between meetings', icon: <Timer className="w-4 h-4" />, level: 'advanced' },
];

const driveAutomations: AutomationFeature[] = [
  { id: 'auto-organize', title: 'Auto-Organize Files', description: 'Organize files into smart folders', icon: <FolderOpen className="w-4 h-4" />, level: 'basic' },
  { id: 'backup-sync', title: 'Backup & Sync', description: 'Automatic backup and sync', icon: <RefreshCw className="w-4 h-4" />, level: 'basic' },
  { id: 'file-naming', title: 'Smart File Naming', description: 'Auto-rename with conventions', icon: <FileText className="w-4 h-4" />, level: 'basic' },
  { id: 'ai-search', title: 'AI-Powered Search', description: 'Natural language file search', icon: <Search className="w-4 h-4" />, level: 'intermediate' },
  { id: 'auto-sharing', title: 'Smart Sharing', description: 'Auto-share with team members', icon: <Share2 className="w-4 h-4" />, level: 'intermediate' },
  { id: 'version-control', title: 'Version Control', description: 'Track file versions', icon: <Clock className="w-4 h-4" />, level: 'intermediate' },
  { id: 'content-analysis', title: 'Content Analysis', description: 'AI categorization', icon: <Brain className="w-4 h-4" />, level: 'advanced' },
  { id: 'compliance-check', title: 'Compliance Check', description: 'Security scanning', icon: <Shield className="w-4 h-4" />, level: 'advanced' },
  { id: 'storage-optimization', title: 'Storage Optimization', description: 'Find duplicates', icon: <BarChart3 className="w-4 h-4" />, level: 'advanced' },
];

const sheetsAutomations: AutomationFeature[] = [
  { id: 'data-import', title: 'Auto Data Import', description: 'Import from emails and APIs', icon: <Plus className="w-4 h-4" />, level: 'basic' },
  { id: 'status-updates', title: 'Status Updates', description: 'Auto-update status columns', icon: <CheckCircle2 className="w-4 h-4" />, level: 'basic' },
  { id: 'notifications', title: 'Change Notifications', description: 'Alerts on changes', icon: <Bell className="w-4 h-4" />, level: 'basic' },
  { id: 'auto-formatting', title: 'Smart Formatting', description: 'Auto-format cells', icon: <Table className="w-4 h-4" />, level: 'intermediate' },
  { id: 'cross-sheet-sync', title: 'Cross-Sheet Sync', description: 'Sync across sheets', icon: <RefreshCw className="w-4 h-4" />, level: 'intermediate' },
  { id: 'scheduled-reports', title: 'Scheduled Reports', description: 'Generate reports', icon: <Clock className="w-4 h-4" />, level: 'intermediate' },
  { id: 'ai-insights', title: 'AI Data Insights', description: 'AI-powered analysis', icon: <Brain className="w-4 h-4" />, level: 'advanced' },
  { id: 'predictive-analytics', title: 'Predictive Analytics', description: 'Forecast trends', icon: <BarChart3 className="w-4 h-4" />, level: 'advanced' },
  { id: 'workflow-triggers', title: 'Workflow Triggers', description: 'Trigger workflows', icon: <Zap className="w-4 h-4" />, level: 'advanced' },
];

const docsAutomations: AutomationFeature[] = [
  { id: 'template-fill', title: 'Template Auto-Fill', description: 'Fill templates with data', icon: <FileText className="w-4 h-4" />, level: 'basic' },
  { id: 'doc-creation', title: 'Auto Doc Creation', description: 'Create from triggers', icon: <Plus className="w-4 h-4" />, level: 'basic' },
  { id: 'pdf-export', title: 'Auto PDF Export', description: 'Export to PDF', icon: <Share2 className="w-4 h-4" />, level: 'basic' },
  { id: 'approval-workflows', title: 'Approval Workflows', description: 'Route for review', icon: <CheckCircle2 className="w-4 h-4" />, level: 'intermediate' },
  { id: 'content-merge', title: 'Content Merge', description: 'Merge from sheets', icon: <RefreshCw className="w-4 h-4" />, level: 'intermediate' },
  { id: 'version-tracking', title: 'Version Tracking', description: 'Compare versions', icon: <Clock className="w-4 h-4" />, level: 'intermediate' },
  { id: 'ai-writing', title: 'AI Writing Assistant', description: 'AI-powered drafting', icon: <Brain className="w-4 h-4" />, level: 'advanced' },
  { id: 'ai-summarization', title: 'AI Summarization', description: 'Auto-generate summaries', icon: <Sparkles className="w-4 h-4" />, level: 'advanced' },
  { id: 'smart-contracts', title: 'Smart Contracts', description: 'AI contract generation', icon: <Shield className="w-4 h-4" />, level: 'advanced' },
];

const LevelBadge = ({ level }: { level: 'basic' | 'intermediate' | 'advanced' }) => {
  const colors = {
    basic: 'bg-green-100 text-green-700',
    intermediate: 'bg-blue-100 text-blue-700',
    advanced: 'bg-purple-100 text-purple-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${colors[level]}`}>
      {level}
    </span>
  );
};

export default function GoogleSuite() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const services = [
    {
      name: 'Google Calendar',
      icon: <Calendar className="w-8 h-8" />,
      color: 'from-blue-500 to-blue-600',
      automations: calendarAutomations,
      count: 15,
    },
    {
      name: 'Google Drive',
      icon: <FolderOpen className="w-8 h-8" />,
      color: 'from-yellow-500 to-orange-500',
      automations: driveAutomations,
      count: 9,
    },
    {
      name: 'Google Sheets',
      icon: <Table className="w-8 h-8" />,
      color: 'from-green-500 to-green-600',
      automations: sheetsAutomations,
      count: 9,
    },
    {
      name: 'Google Docs',
      icon: <FileText className="w-8 h-8" />,
      color: 'from-blue-400 to-blue-500',
      automations: docsAutomations,
      count: 9,
    },
  ];

  return (
    <div className={`min-h-screen p-4 sm:p-6 lg:p-8 ${isDark ? 'bg-dark-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-500/10 via-yellow-500/10 to-green-500/10 mb-4">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Coming Soon
            </span>
          </div>

          <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Google Suite Automation
          </h1>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Powerful AI-driven automation for Google Calendar, Drive, Sheets, and Docs.
            Let your AI Employee manage your entire Google Workspace.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {services.map((service, index) => (
            <motion.div
              key={service.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-2xl p-6 ${isDark ? 'bg-dark-800 border border-white/10' : 'bg-white shadow-lg'}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white`}>
                  {service.icon}
                </div>
                <div className="flex-1">
                  <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {service.name}
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {service.count} automations
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-red-500/10 via-yellow-500/10 to-green-500/10 text-gray-500">
                  Soon
                </span>
              </div>

              {/* Automation Preview */}
              <div className="space-y-2">
                {service.automations.slice(0, 4).map((automation) => (
                  <div
                    key={automation.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg ${
                      isDark ? 'bg-dark-700/50' : 'bg-gray-50'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      isDark ? 'bg-dark-600 text-gray-400' : 'bg-white text-gray-500 shadow-sm'
                    }`}>
                      {automation.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {automation.title}
                      </p>
                    </div>
                    <LevelBadge level={automation.level} />
                  </div>
                ))}
                {service.automations.length > 4 && (
                  <p className={`text-center text-sm py-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    +{service.automations.length - 4} more automations
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Feature Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`rounded-2xl p-8 text-center ${isDark ? 'bg-dark-800 border border-white/10' : 'bg-white shadow-lg'}`}
        >
          <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            What's Coming
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-green-50'}`}>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-green-600" />
              </div>
              <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Basic Automations</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Auto-create, sync, reminders, and file organization
              </p>
            </div>

            <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-blue-50'}`}>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Smart Scheduling</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                AI-powered meeting scheduling and focus time
              </p>
            </div>

            <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-purple-50'}`}>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>AI Intelligence</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Analytics, conflict resolution, and optimization
              </p>
            </div>
          </div>

          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            We're working hard to bring you the best Google Workspace automation experience.
            <br />
            Stay tuned for updates!
          </p>
        </motion.div>
      </div>
    </div>
  );
}
